// SAB-575: shared shell for the /signup + /login screens. Full-screen mobile;
// on desktop the same routes reuse this (the desktop hero also has the inline
// card, but the routes remain reachable). Back chevron always returns to
// marketing ("/"). Header link is inverted per screen (Signup <-> Login) and
// preserves the ?intent param so commissioner intent survives the hop.
import { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

interface Props {
  headline: string;
  subhead?: string;
  /** Optional chip above the headline (e.g. STEP 1 OF 2 · CREATING A LEAGUE). */
  chip?: ReactNode;
  headerPrompt: string;      // "Have an account?" / "New here?"
  headerLinkLabel: string;   // "Log In" / "Sign Up"
  headerLinkTo: string;      // "/login" / "/signup" (with intent query preserved)
  children: ReactNode;
}

export function AuthEntryLayout({
  headline, subhead, chip, headerPrompt, headerLinkLabel, headerLinkTo, children,
}: Props) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground dark">
      <div className="mx-auto w-full max-w-[440px] px-5 pt-4 pb-10" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
        {/* Top row: back chevron + inverted header link */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <button
            type="button"
            onClick={() => navigate('/')}
            aria-label="Back to home"
            className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border border-white/[0.12] bg-white/[0.05] text-foreground hover:bg-white/[0.09] transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <p className="text-right text-[15px] text-muted-foreground pt-1.5">
            {headerPrompt}{' '}
            <Link to={headerLinkTo} className="font-bold text-[hsl(var(--primary))] hover:underline">
              {headerLinkLabel}
            </Link>
          </p>
        </div>

        {chip && <div className="mb-4">{chip}</div>}

        <h1 className="font-varsity leading-[0.98] text-[36px] mb-2">{headline}</h1>
        {subhead && <p className="text-[15px] text-muted-foreground leading-[1.5] mb-7 max-w-[380px]">{subhead}</p>}
        {!subhead && <div className="mb-7" />}

        {children}
      </div>
    </div>
  );
}

export default AuthEntryLayout;
